/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useDomainAliases.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for managing domain aliases (CRUD operations).
────────────────────────────────────────────────────────────────*/

import { useState, useCallback } from 'react';
import { useI18n } from '../../../hooks/useI18n';
import { useAuthStore } from '../../../store/auth';
import { useNotifications } from '../../../contexts/NotificationContext';
import {
  createDomainAlias,
  updateDomainAlias,
  deleteDomainAlias
} from '../api/telephonyDomainsApi';
import type { DomainAlias } from '../types';

const DEFAULT_ALIAS_FORM: DomainAlias = {
  alias: '',
  description: '',
  enabled: true,
  domainId: 0
};

function sanitizeAliasForm(state: DomainAlias): DomainAlias {
  return {
    id: state.id,
    alias: state.alias.trim(),
    description: state.description?.trim(),
    enabled: Boolean(state.enabled),
    domainId: state.domainId
  };
}

function cloneAliasForm(domainId: number | string): DomainAlias {
  return { ...DEFAULT_ALIAS_FORM, domainId: typeof domainId === 'string' ? domainId : domainId };
}

export function useDomainAliases(
  domainId: number | string | undefined,
  loadDomainAliases: (domainId: number | string | undefined, force?: boolean) => Promise<void>
) {
  const { t } = useI18n();
  const { user } = useAuthStore();
  const { showError, showSuccess } = useNotifications();
  const tenantSlug = user?.tenant?.slug ?? 'main';

  const [showAliasForm, setShowAliasForm] = useState(false);
  const [editingAlias, setEditingAlias] = useState<DomainAlias | null>(null);
  const [aliasForm, setAliasForm] = useState<DomainAlias>(() => cloneAliasForm(0));
  const [aliasErrors, setAliasErrors] = useState<Partial<Record<keyof DomainAlias, string>>>({});

  // Reset alias state
  const resetAliasState = useCallback(
    (domainId?: number | string) => {
      const resolvedId = domainId ?? 0;
      setAliasForm(cloneAliasForm(resolvedId));
      setAliasErrors({});
      setEditingAlias(null);
    },
    []
  );

  // Close alias form
  const closeAliasForm = useCallback((domainId?: number | string) => {
    resetAliasState(domainId);
    setShowAliasForm(false);
  }, [resetAliasState]);

  // Open alias modal
  const openAliasModal = useCallback(() => {
    if (!domainId) {
      showError(t('telephonyDomains.aliases.requiresSavedDomain'));
      return;
    }
    setEditingAlias(null);
    resetAliasState(domainId);
    setShowAliasForm(true);
  }, [domainId, resetAliasState, showError, t]);

  // Handle alias input change
  const handleAliasInputChange = useCallback(
    (field: keyof DomainAlias, value: DomainAlias[keyof DomainAlias]) => {
      setAliasForm(prev => ({
        ...prev,
        [field]: value
      }));

      setAliasErrors(prev => {
        if (!prev?.[field]) {
          return prev;
        }
        const { [field]: _removed, ...rest } = prev;
        return rest;
      });
    },
    []
  );

  // Validate alias form
  const validateAliasForm = useCallback(() => {
    const newErrors: Record<string, string> = {};

    if (!aliasForm.alias?.trim()) {
      newErrors.alias = t('telephonyDomains.aliases.validation.aliasRequired');
    }

    setAliasErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [aliasForm.alias, t]);

  // Handle alias submit
  const handleAliasSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();

      if (!validateAliasForm()) {
        showError(t('telephonyDomains.aliases.formError'));
        return;
      }

      if (!domainId) {
        showError(t('telephonyDomains.aliases.requiresSavedDomain'));
        return;
      }

      try {
        const sanitized = sanitizeAliasForm({ ...aliasForm, domainId });

        if (editingAlias?.id) {
          await updateDomainAlias(
            String(domainId),
            String(editingAlias.id),
            {
              alias: sanitized.alias,
              description: sanitized.description || undefined,
              enabled: sanitized.enabled
            },
            tenantSlug,
            user?.id
          );
          showSuccess(t('telephonyDomains.aliases.updateSuccess'));
        } else {
          await createDomainAlias(
            String(domainId),
            {
              alias: sanitized.alias,
              description: sanitized.description || undefined,
              enabled: sanitized.enabled
            },
            tenantSlug,
            user?.id
          );
          showSuccess(t('telephonyDomains.aliases.createSuccess'));
        }

        closeAliasForm(domainId);
        loadDomainAliases(domainId, true);
      } catch (error) {
        // Check if error is about duplicate alias or conflict with domain
        const errorMessage = error instanceof Error ? error.message : String(error);
        const isDuplicate = errorMessage.toLowerCase().includes('duplicate') ||
          errorMessage.toLowerCase().includes('already exists') ||
          errorMessage.toLowerCase().includes('unique');
        const conflictsWithDomain = errorMessage.toLowerCase().includes('conflicts with') ||
          errorMessage.toLowerCase().includes('domain name');

        if (conflictsWithDomain) {
          showError(t('telephonyDomains.aliases.conflictWithDomainError'));
        } else if (isDuplicate) {
          showError(t('telephonyDomains.aliases.duplicateError'));
        } else {
          showError(
            editingAlias
              ? t('telephonyDomains.aliases.updateError')
              : t('telephonyDomains.aliases.createError')
          );
        }
      }
    },
    [
      aliasForm,
      closeAliasForm,
      domainId,
      editingAlias,
      loadDomainAliases,
      showError,
      showSuccess,
      t,
      tenantSlug,
      user?.id,
      validateAliasForm
    ]
  );

  // Handle edit alias
  const handleEditAlias = useCallback((alias: DomainAlias) => {
    setEditingAlias(alias);
    setAliasForm(
      sanitizeAliasForm({
        ...alias,
        domainId: alias.domainId ?? (domainId ?? 0)
      })
    );
    setAliasErrors({});
    setShowAliasForm(true);
  }, [domainId]);

  // Handle delete alias
  const handleDeleteAlias = useCallback(
    async (aliasId: number | string) => {
      if (!domainId) return;

      try {
        await deleteDomainAlias(String(domainId), String(aliasId), tenantSlug, user?.id);
        showSuccess(t('telephonyDomains.aliases.deleteSuccess'));
        loadDomainAliases(domainId, true);
      } catch (error) {
        showError(t('telephonyDomains.aliases.deleteError'));
      }
    },
    [domainId, loadDomainAliases, showError, showSuccess, t, tenantSlug, user?.id]
  );

  // Update alias form domainId when domainId changes
  const updateAliasFormDomainId = useCallback((newDomainId: number | undefined) => {
    setAliasForm(prev => ({ ...prev, domainId: newDomainId ?? 0 }));
  }, []);

  return {
    showAliasForm,
    editingAlias,
    aliasForm,
    aliasErrors,
    openAliasModal,
    closeAliasForm,
    handleAliasInputChange,
    handleAliasSubmit,
    handleEditAlias,
    handleDeleteAlias,
    updateAliasFormDomainId
  };
}


